
import React from "react";
import { Helmet } from "react-helmet-async";
import Layout from "../components/Layout";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import ContactForm from "../components/contact/ContactForm";

const ContactTrueCore = () => {
  return (
    <Layout>
      <Helmet>
        <title>Contact TrueCore Gadgets - Get in Touch | Mumbai, Delhi, Hyderabad</title>
        <meta name="description" content="Contact TrueCore Gadgets for custom electronics solutions. Visit our Mumbai office or reach us at 022 6964 7168. Expert support for tablets, PCs, and enterprise devices." />
        <meta name="keywords" content="contact TrueCore, electronics support Mumbai, custom device inquiry, TrueCore office address, technical support India" />
        
        <link rel="canonical" href="https://www.truecoregadgets.com/contact-truecore" />
        
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Contact TrueCore Gadgets - Get in Touch" />
        <meta property="og:description" content="Contact TrueCore Gadgets for custom electronics solutions and technical support across India." />
        <meta property="og:url" content="https://www.truecoregadgets.com/contact-truecore" />
        <meta property="og:image" content="https://www.truecoregadgets.com/lovable-uploads/461a823a-5781-4980-ad39-ffec726a88a3.png" />
      </Helmet>

      <div className="container mx-auto py-12 px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold gradient-text mb-6">Contact TrueCore Gadgets</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Get in touch with our expert team for custom electronics solutions, technical support, or partnership opportunities
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div>
            <h2 className="text-2xl font-bold mb-8">Get in Touch</h2>
            
            {/* Main Office */}
            <div className="bg-card p-6 rounded-lg shadow-md border mb-6">
              <h3 className="text-xl font-bold mb-4 flex items-center">
                <MapPin className="h-6 w-6 text-primary mr-3" />
                Mumbai Headquarters
              </h3>
              <div className="space-y-3 text-muted-foreground">
                <p>TrueCore Gadget Innovations Pvt. Ltd.</p>
                <p>Tech Park, Andheri East</p>
                <p>Mumbai, Maharashtra 400069</p>
                <p>India</p>
              </div>
            </div>

            {/* Contact Details */}
            <div className="space-y-4 mb-8">
              <div className="flex items-center space-x-4">
                <Phone className="h-6 w-6 text-primary" />
                <div>
                  <p className="font-semibold">Phone</p>
                  <a href="tel:02269647168" className="text-muted-foreground hover:text-primary">022 6964 7168</a>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                <Mail className="h-6 w-6 text-primary" />
                <div>
                  <p className="font-semibold">Email</p>
                  <a href="mailto:support@truecoregadgets.com" className="text-muted-foreground hover:text-primary">support@truecoregadgets.com</a>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                <Clock className="h-6 w-6 text-primary" />
                <div>
                  <p className="font-semibold">Business Hours</p>
                  <p className="text-muted-foreground">Monday - Saturday: 9:00 AM - 6:00 PM IST</p>
                  <p className="text-muted-foreground">Sunday: Closed</p>
                </div>
              </div>
            </div>

            {/* Service Locations */}
            <div className="bg-muted/50 p-6 rounded-lg mb-8">
              <h3 className="text-xl font-bold mb-4">Service Locations</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center">
                  <h4 className="font-semibold">Mumbai</h4>
                  <p className="text-sm text-muted-foreground">Main Office & Service Center</p>
                </div>
                <div className="text-center">
                  <h4 className="font-semibold">Delhi NCR</h4>
                  <p className="text-sm text-muted-foreground">Service Partner Network</p>
                </div>
                <div className="text-center">
                  <h4 className="font-semibold">Hyderabad</h4>
                  <p className="text-sm text-muted-foreground">Regional Service Hub</p>
                </div>
              </div>
            </div>

            {/* Quick Contact Options */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <a href="tel:02269647168" className="tech-btn-outline flex items-center justify-center">
                <Phone className="mr-2 h-5 w-5" />
                Call Us Now
              </a>
              <a href="https://wa.me/912269647168" className="tech-btn-outline flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16" className="mr-2">
                  <path d="M13.601 2.326A7.854 7.854 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.933 7.933 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.898 7.898 0 0 0 13.6 2.326zM7.994 14.521a6.573 6.573 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.557 6.557 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592zm3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.777-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.729.729 0 0 0-.529.247c-.182.198-.691.677-.691 1.654 0 .977.71 1.916.81 2.049.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232z"/>
                </svg>
                WhatsApp
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <h2 className="text-2xl font-bold mb-8">Send us a Message</h2>
            <ContactForm />
          </div>
        </div>

        {/* Google Map */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-center mb-8">Find Us on Map</h2>
          <div className="w-full h-96 bg-gray-200 rounded-xl overflow-hidden">
            <iframe
              title="TrueCore Gadgets Mumbai Office Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3769.3098301833007!2d72.8487212!3d19.1285427!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7c9da695e73a1%3A0x6d2aaae3ab9737c4!2sAndheri%20East%2C%20Mumbai%2C%20Maharashtra!5e0!3m2!1sen!2sin!4v1612345678901!5m2!1sen!2sin"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen={false}
              loading="lazy"
            ></iframe>
          </div>
        </div>

        {/* Emergency Contact */}
        <div className="mt-16 bg-primary/10 rounded-lg p-8 text-center">
          <h2 className="text-2xl font-bold mb-4">Need Urgent Support?</h2>
          <p className="text-muted-foreground mb-6">
            For urgent technical issues or warranty claims, contact our priority support line
          </p>
          <a href="tel:02269647168" className="tech-btn-primary">
            Priority Support: 022 6964 7168
          </a>
        </div>
      </div>
    </Layout>
  );
};

export default ContactTrueCore;
